import {
  Chip,
  ComparisonMatrix,
  DocumentIcon,
  HTMLDisplay,
  Tag,
} from '@all-manager/design-system-v2';
import { Quote, QuoteCompaisonColumnDatabase } from '../../../../types';
import { formatQuoteAmount, formatQuoteDate, isQuoteExpired } from '../../utils/quoteValues';
import QuoteMenuAction from '../QuoteMenuAction/QuoteMenuAction';
import { useQuoteComparison } from './useQuoteComparison';

type Props = {
  groupId: string;
  columns: QuoteCompaisonColumnDatabase[];
  quotes: Quote[];
  isAdmin: boolean;
};

const COMMENT_MAX_LINES = 6;

function renderValue(column: QuoteCompaisonColumnDatabase, quote: Quote) {
  const value = quote.values[column.id];

  if (value === undefined || value === '') {
    return null;
  }

  if (column.type === 'number') {
    return formatQuoteAmount(value);
  }

  if (column.type === 'multiline') {
    return <HTMLDisplay content={String(value)} maxLines={COMMENT_MAX_LINES} />;
  }

  return String(value);
}

function renderQuoteMeta(quote: Quote) {
  const issuedAt = formatQuoteDate(quote.issued_at);
  const expiresAt = formatQuoteDate(quote.expiration_date);
  const expired = isQuoteExpired(quote.expiration_date);

  if (!issuedAt && !expiresAt && !quote.file_url) {
    return null;
  }

  return (
    <>
      {issuedAt ? <span>Émis le {issuedAt}</span> : null}
      {expiresAt ? <span>{expired ? 'Expiré le' : 'Valable jusqu\'au'} {expiresAt}</span> : null}
      {expired ? <Tag color="cancelled" label="Expiré" variantIcon="state" /> : null}
      {quote.file_url ? (
        <Chip
          icon={<DocumentIcon fontSize="small" />}
          label="Voir le devis"
          href={quote.file_url}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          aria-label={`Voir le devis de ${quote.provider} (nouvel onglet)`}
        />
      ) : null}
    </>
  );
}

export default function QuoteComparison(props: Props) {
  const hook = useQuoteComparison(props);

  return (
    <ComparisonMatrix
      aria-label="Comparaison des devis"
      criteriaLabel="Critères"
      summaryCriterionId={hook.summaryColumnId}
      items={props.quotes.map((quote) => ({
        id: quote.id,
        title: quote.provider,
        meta: renderQuoteMeta(quote),
        action: props.isAdmin ? (
          <QuoteMenuAction groupId={props.groupId} quote={quote} columns={props.columns} />
        ) : undefined,
      }))}
      criteria={hook.visibleColumns.map((column) => hook.toCriterion(column, renderValue))}
    />
  );
}
